import { PageContent, f7 } from 'framework7-react';
import { type CSSProperties, type ReactNode, useCallback, useRef } from 'react';

/**
 * Wires Framework7 pull-to-refresh onto a custom scroller. Our workspaces run
 * `pageContent={false}` and scroll inside `.rsv-main` / `.rsv-single` rather
 * than the page-content, so F7's automatic page-level PTR (which watches
 * `.page-content`) never engages. Instead we mark the real scroller as the
 * `.ptr-content`, create the PTR instance on it by hand (`f7.ptr.create`), and
 * translate the `ptr:refresh` DOM event into the caller's async refresh.
 *
 * A callback ref (not an effect) drives create/destroy so it fires exactly when
 * the scroller element mounts/unmounts - including when the layout swaps between
 * `.rsv-main` and `.rsv-single`. Callers must remount the scroller (via `key`)
 * when they toggle PTR on/off, which WorkspaceCanvas does below.
 */
function usePtrRef(onRefresh?: () => Promise<unknown>) {
  // Latest callback without re-running the ref: the handler reads it lazily.
  const cbRef = useRef(onRefresh);
  cbRef.current = onRefresh;
  const cleanup = useRef<(() => void) | null>(null);

  return useCallback((el: HTMLElement | null) => {
    cleanup.current?.();
    cleanup.current = null;
    if (!el) return;
    f7.ptr.create(el);
    const handler = (e: Event) => {
      // F7 passes its `done` callback as the event detail; resolve the refresh
      // (success or failure) before closing the preloader so the spinner always
      // retracts.
      const done = (e as CustomEvent<() => void>).detail;
      Promise.resolve(cbRef.current?.()).finally(() => done());
    };
    el.addEventListener('ptr:refresh', handler);
    cleanup.current = () => {
      el.removeEventListener('ptr:refresh', handler);
      f7.ptr.destroy(el);
    };
  }, []);
}

// F7 preloader markup, required as the first child of a `.ptr-content`.
const PtrPreloader = () => (
  <div className="ptr-preloader">
    <div className="preloader" />
    <div className="ptr-arrow" />
  </div>
);

/**
 * The shared body of the full-height workspace shells (Reservations, Tables).
 * The page owns its own `<Navbar>` (they differ); this owns everything below
 * it: the active view fills the canvas, and on wide layouts a selected entity
 * opens a docked, independently-scrolling inspector beside it (on phones the
 * page pushes a detail route instead, so it just passes `inspector={null}`).
 *
 * Layout lives in `.rsv-page-content` (styles.css). With `pageContent={false}`
 * we own the single `.page-content`: top padding is 0 and moves down to the
 * inner scrollers (they read `--rsv-nav-offset`) so content slides under the
 * blurred navbar instead of clipping at the scroller's top; bottom padding
 * clears the tab bar for scroll views but is dropped when a full-bleed plan
 * canvas is present so it can run under the tab bar too.
 */
export default function WorkspaceCanvas({
  isWide,
  main,
  inspector,
  bottomBarInset = 0,
  onRefresh,
}: {
  isWide: boolean;
  /** The active view - fills the canvas. */
  main: ReactNode;
  /** Docked inspector content; `null` hides the panel (nothing selected, or
   *  a phone layout that pushes a detail route instead). */
  inspector: ReactNode | null;
  /** Measured height (px) of the bottom tab bar. Published as
   *  `--rsv-bottom-offset` so the inner scrollers pad their content clear of
   *  the bar while still scrolling under it. */
  bottomBarInset?: number;
  /** When provided, the main scroller becomes pull-to-refresh: a downward pull
   *  at the top runs this and shows the F7 spinner until it settles. Omit for
   *  views that own their scroll (e.g. calendar/timeline) - see the callers,
   *  which only pass it for their list view. */
  onRefresh?: () => Promise<unknown>;
}) {
  const ptrRef = usePtrRef(onRefresh);
  const ptrEnabled = !!onRefresh;
  // Toggling PTR changes the scroller's markup/handlers, so key it to remount
  // when enablement flips (the callback ref then re-runs create/destroy).
  const ptrKey = ptrEnabled ? 'ptr' : 'plain';

  return (
    <PageContent
      className="rsv-page-content"
      style={{ '--rsv-bottom-offset': `${bottomBarInset}px` } as CSSProperties}
    >
      {isWide ? (
        <div className="rsv-split">
          <main
            key={`main-${ptrKey}`}
            ref={ptrEnabled ? ptrRef : undefined}
            className={`rsv-main${ptrEnabled ? ' ptr-content' : ''}`}
          >
            {ptrEnabled && <PtrPreloader />}
            {main}
          </main>
          {inspector && (
            <aside className="rsv-inspector">
              <div className="rsv-inspector-scroll">{inspector}</div>
            </aside>
          )}
        </div>
      ) : (
        <div
          key={`single-${ptrKey}`}
          ref={ptrEnabled ? ptrRef : undefined}
          className={`rsv-single${ptrEnabled ? ' ptr-content' : ''}`}
        >
          {ptrEnabled && <PtrPreloader />}
          {main}
        </div>
      )}
    </PageContent>
  );
}
